import React, { forwardRef } from 'react';

const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`); // match escaped "." characters via in a non-capturing group

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

interface InputProps
  extends Omit<React.HTMLProps<HTMLInputElement>, 'ref' | 'onChange' | 'as'> {
  value: string | number;
  onUserInput: (input: string) => void;
  prependSymbol?: React.ReactNode;
  maxDecimals?: number;
  label?: string;
}

const NumericalInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      label,
      onUserInput,
      placeholder,
      prependSymbol,
      maxDecimals = 18,
      ...rest
    }: InputProps,
    ref,
  ) => {
    const enforcer = (nextUserInput: string) => {
      if (
        nextUserInput === '' ||
        inputRegex.test(escapeRegExp(nextUserInput))
      ) {
        const decimalGroups = nextUserInput.split('.');
        if (
          maxDecimals &&
          decimalGroups.length > 1 &&
          decimalGroups[1].length > maxDecimals
        ) {
          return;
        }

        onUserInput(nextUserInput);
      }
    };

    return (
      <div className="numerical-input">
        {label && <label className="numerical-label">{label}</label>}
        <div className="numerical-holder">
          {prependSymbol && prependSymbol}
          <input
            {...rest}
            ref={ref}
            value={value}
            onChange={event => {
              enforcer(event.target.value.replace(/,/g, '.'));
            }}
            inputMode="decimal"
            autoComplete="off"
            autoCorrect="off"
            type="text"
            pattern="^[0-9]*[.,]?[0-9]*$"
            placeholder={placeholder || '0'}
            minLength={1}
            maxLength={79}
            spellCheck="false"
            className="number-input"
          />
        </div>
      </div>
    );
  },
);

NumericalInput.displayName = 'NumericalInput';

export default NumericalInput;
